/**
 * CTO Directive #99 — Skill chip → ActionBus translator.
 *
 * The Mind emits chips with namespaced "skill" kinds (legacy:
 * ``summarise.tumor_board``, ``play.meditation``, ``capsule.prepare``,
 * ``open.evidence``, ``remember.note``). The Living Orb sends one
 * shape to the Action Bus: ``{kind, subject, capability?, params}``
 * where ``kind`` is one of the five verbs.
 *
 * This file is the only place that knows the mapping. New skills add
 * one entry — they NEVER add a new verb.
 */

/**
 * Translate one chip into an ActionBus body.
 * @param {object} chip — {kind, payload}
 * @returns {{kind:string, subject:string, capability?:string, params:object} | null}
 */
export function chipToAction(chip) {
  if (!chip || !chip.kind) return null;
  const k = String(chip.kind);
  const payload = chip.payload || {};

  // summarise.* → prepare/<subject> (the body lives in the production engine)
  if (k.startsWith("summarise.")) {
    const target = k.slice("summarise.".length);
    const subjectMap = {
      tumor_board:  "tumor_board",
      presentation: "presentation",
      patient:      "patient_brief",
      clinical:     "report",
      scientific:   "presentation",
    };
    return {
      kind: "prepare",
      subject: subjectMap[target] || "report",
      params: { focus: payload.focus, target },
    };
  }

  // play.* → execute/<subject>; capability is inferred by the bus.
  if (k === "play.meditation" || k === "play.music" || k === "play.documentary"
      || k === "play.lecture") {
    const subjectMap = {
      "play.meditation":  "meditation",
      "play.music":       "music",
      "play.documentary": "documentary",
      "play.lecture":     "lecture",
    };
    return {
      kind: "execute",
      subject: subjectMap[k],
      params: {
        topic: payload.topic,
        mood: payload.mood,
        duration_minutes: payload.duration_minutes,
      },
    };
  }

  // capsule.prepare → prepare/capsule.
  if (k === "capsule.prepare") {
    return { kind: "prepare", subject: "capsule", params: payload };
  }

  // open.evidence → retrieve/evidence.
  if (k === "open.evidence") {
    return { kind: "retrieve", subject: "evidence", params: payload };
  }

  // remember.note → remember/note.
  if (k === "remember.note") {
    return { kind: "remember", subject: "note", params: payload };
  }

  // Unknown legacy kind — pass-through so the bus can still try.
  // Verb is inferred from the first segment if it happens to be
  // one of the five; otherwise we default to execute.
  const [verb, ...rest] = k.split(".");
  const five = new Set(["remember", "retrieve", "prepare", "communicate", "execute"]);
  if (five.has(verb)) {
    return {
      kind: verb,
      subject: rest.join(".") || "general",
      params: payload,
    };
  }
  return null;
}
